import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";
import FormGroup from "@mui/material/FormGroup";
import Switch from "@mui/material/Switch";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Radio from "@mui/material/Radio";

import { setUser, showToast } from "../../store/actions";
import UserService from "../../service/user";
import { ButtonUI, PasswordValidationText } from "../Common/Common";
import { regexEmail, regexPassword } from "../../util/regularExpress";

const useStyles = makeStyles((theme) =>
  createStyles({
    formLabel: {
      display: "flex",
      flexDirection: "row",
      "& .MuiFormLabel-root": {
        display: "inline-flex",
        width: "100%",
      },
      "& .MuiFormLabel-root.Status": {
        display: "inline-flex",
        width: "auto",
        alignItems: "center",
        color: "#000000de",
      },
      "& .MuiFormControlLabel-root": {
        display: "inline-flex",
        width: "fit-content",
        height: "30px",
      },
    },
    RadioContainer: {
      display: "flex",
      justifyContent: "space-between",
      width: "100%",
      flexWrap: "wrap",
    },
    dialogPaper: {
      "& .MuiPaper-root": {
        maxWidth: "710px",
      },
      "& .MuiFormHelperText-root": {
        margin: 0,
        padding: 0,
      },
      "& .MuiGrid-item": {
        paddingTop: "15px",
      },
      "& .MuiGrid-item .MuiTextField-root": {
        marginTop: 0,
      },
      "& .MuiDivider-root": {
        marginTop: "5px",
        marginBottom: "9px",
      },
      "& .MuiDialogActions-root": {
        padding: "15px 20px 20px 0",
      },
    },
    formContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
      padding: "0px 15px",
      textAlign: "left",
      minHeight: "250px",
    },
    container: {
      padding: "5px 5px 0 5px",
      backgroundColor: "#FFFFFF",
    },
    HalfTextField: {
      width: "calc(50% - 8px)",
    },
    Toolbar: {
      fontSize: "1.1rem",
      backgroundColor: "white",
      color: "black",
    },
    [theme.breakpoints.down("600")]: {
      HalfTextField: {
        width: "calc(100%)",
      },
    },
  })
);

export default function AddForm({ setOpen, loadUserdata, userId }) {
  const [username, setUsername] = useState("");
  const [firstname, setFirstName] = useState("");
  const [lastname, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({
    firstname: false,
    lastname: false,
    username: false,
    password: false,
    confirmPassword: false,
    email: false,
  });
  const [userrole, setUserrole] = useState(2);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);

  const classes = useStyles();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state);

  const handleSave = async () => {
    setErrors((prevState) => {
      return {
        ...prevState,
        firstname: firstname.trim().length < 3 || firstname.trim().length > 50,
        lastname: lastname.trim().length < 3 || lastname.trim().length > 50,
        username: username.trim().length < 3 || username.trim().length > 20,
        password: !regexPassword(password.trim()),
        confirmPassword: confirmPassword.trim() !== password.trim(),
        email: email.trim().length > 100 || !regexEmail(email.trim()),
      };
    });

    if (
      !(
        firstname.trim().length < 3 ||
        firstname.trim().length > 50 ||
        lastname.trim().length < 3 ||
        lastname.trim().length > 50 ||
        username.trim().length < 3 ||
        username.trim().length > 20 ||
        email.trim().length > 100 ||
        !regexEmail(email.trim()) ||
        !regexPassword(password.trim()) ||
        confirmPassword.trim() !== password.trim()
      )
    ) {
      setLoading(true);

      let submitData = {
        LoginId: username.trim(),
        FirstName: firstname.trim(),
        LastName: lastname.trim(),
        Email: email.trim(),
        Status: active,
        UserRole: userrole,
      };

      if (password.trim()) submitData["Password"] = password.trim();

      let result = null;
      const userService = new UserService();

      if (userId > 0) result = await userService.updateUser(userId, submitData);
      else result = await userService.createUser(submitData);

      if (result.success) {
        dispatch(
          showToast({
            message: result.message,
            type: "success",
          })
        );

        // If LoggedIn user and the user who gets updated is same then setUser to Updated User
        if (user.currentUser.User_id === userId) {
          dispatch(setUser(result.data));
        }
        setOpen(false);
        loadUserdata();
      }
    }

    setLoading(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const clearFormData = () => {
    setUsername("");
    setFirstName("");
    setLastName("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setUserrole(2);
    setActive(0);
    setErrors({
      firstname: false,
      lastname: false,
      username: false,
      password: false,
      confirmPassword: false,
    });
  };

  useEffect(() => {
    const userService = new UserService();
    userService.getUserRoles().then((response) => {
      if (response.success) setRoles(response.data);
    });

    const getUserData = async () => {
      setLoading(true);
      const result = await userService.getUser(userId);

      if (result.success && result.data && Object.keys(result.data).length > 0) {
        const userData = result.data;
        setUsername(userData.UserName);
        setFirstName(userData.FirstName);
        setLastName(userData.LastName);
        setEmail(userData.Email);
        setUserrole(userData.Role_id);
        setActive(userData.Status);
      }

      setLoading(false);
    };
    if (userId > 0) getUserData();

    return () => clearFormData();
  }, [userId]);

  return (
    <Dialog className={classes.dialogPaper} open={roles.length !== 0}>
      <AppBar sx={{ position: "relative" }}>
        <Toolbar className={classes.Toolbar}>{userId > 0 ? "Edit" : "Create"} User</Toolbar>
        <IconButton
          aria-label="close"
          onClick={() => handleCancel()}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "black",
          }}
        >
          <CloseIcon />
        </IconButton>
      </AppBar>
      <Box sx={{ width: "100%" }} className={classes.container}>
        <Backdrop sx={{ color: "red", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="primary" />
        </Backdrop>
        <Box sx={{ width: "100%" }} component="form" className={classes.formContainer}>
          <Grid container spacing={2} style={{ marginTop: "10px" }}>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <TextField
                autoFocus
                margin="dense"
                fullWidth
                label="Login ID"
                variant="outlined"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                error={errors.username}
                helperText={errors.username ? "Login Id length should be between 3 and 20!" : ""}
                required
                disabled={userId > 0}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <TextField
                margin="dense"
                fullWidth
                label="Email ID"
                type="email"
                variant="outlined"
                value={email}
                error={errors.email}
                helperText={errors.email ? "Please enter an valid email." : ""}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <TextField
                margin="dense"
                fullWidth
                label="First Name"
                type="firstname"
                variant="outlined"
                value={firstname}
                error={errors.firstname}
                helperText={errors.firstname ? "FirstName length should be between 3 and 50!" : ""}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <TextField
                margin="dense"
                fullWidth
                label="Last Name"
                type="lastname"
                variant="outlined"
                error={errors.lastname}
                helperText={errors.lastname ? "LastName length should be between 3 and 50!" : ""}
                value={lastname}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <TextField
                margin="dense"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                fullWidth
                label="Password"
                variant="outlined"
                placeholder="********"
                error={errors.password}
                helperText={errors.password ? <PasswordValidationText /> : ""}
                onChange={(e) => {
                  setPassword(e.target.value);
                }}
                required={false}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword((prev) => !prev)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <TextField
                margin="dense"
                type={showConfirmPassword ? "text" : "password"}
                autoComplete="new-password"
                fullWidth
                label="Confirm Password"
                variant="outlined"
                placeholder="********"
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                }}
                error={errors.confirmPassword}
                helperText={errors.confirmPassword ? "Password does not match" : ""}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
              />
            </Grid>
            <Grid item sm={12} xs={12} lg={12} md={12}>
              <FormGroup className={classes.formLabel}>
                <FormLabel>Role:</FormLabel>
                <Box className={classes.RadioContainer}>
                  {roles.map((row, i) => (
                    <FormControlLabel
                      key={i}
                      value={row.Role_id}
                      control={<Radio checked={userrole === row.Role_id} />}
                      label={row.Name}
                      labelPlacement="end"
                      disabled={userId === user.currentUser.User_id}
                      onChange={(e) => setUserrole(parseInt(e.target.value))}
                    />
                  ))}
                </Box>
              </FormGroup>
            </Grid>
            {userId > 0 ? (
              <Grid item sm={12} xs={12} lg={12} md={12}>
                <FormGroup className={classes.formLabel}>
                  <FormLabel>Status:</FormLabel>
                  <FormLabel className="Status">Inactive</FormLabel>
                  <Switch
                    checked={active !== undefined && active ? true : false}
                    color={active !== undefined ? "success" : ""}
                    onChange={(e) => setActive(+!active)}
                    disabled={userId === user.currentUser.User_id}
                  />
                  <FormLabel className="Status">Active</FormLabel>
                </FormGroup>
              </Grid>
            ) : (
              ""
            )}
          </Grid>
        </Box>
        <Box>
          <DialogActions>
            <ButtonUI variant="contained" color="secondary" onClick={handleCancel}>
              Cancel
            </ButtonUI>
            <ButtonUI
              onClick={handleSave}
              variant="contained"
              color="success"
              style={{ marginLeft: 15 }}
              disabled={
                !(username.trim() && firstname.trim() && lastname.trim() && email.trim() && userrole) || loading
              }
            >
              Save
            </ButtonUI>
          </DialogActions>
        </Box>
      </Box>
    </Dialog>
  );
}
